import React, { useState, MouseEvent } from "react";
import moment from "moment";
import { isEmpty } from "lodash";
import { transparentize } from "polished";
import { Box, Card, Typography, makeStyles, IconButton } from "@material-ui/core";
import AvatarGroup from "@material-ui/lab/AvatarGroup";
import { CheckCircle, Notifications } from "@material-ui/icons";
import locale from "@bonliva-crm/locale";
import { Color, EditFilledIcon, WithPopperSimple } from "@bonliva-crm/ui";
import { ConsultantOpportunityCardFragment } from "@bonliva-crm/graphql";
import { ConsultantOpportunityCardMenu } from "./consultant-opportunity-card-menu";
import { CardDescription } from "../../kanban-board/kanban-types";
import UserAvatar from "@containers/UserAvatar";

type Props = {
  card: CardDescription<ConsultantOpportunityCardFragment>;
  dragging: boolean;
  onOpen: () => void;
  onEditOpportunity: () => void;
  onDeleteOpportunity: () => void;
  canBeDeleted: boolean;
};

export function ConsultantOpportunityCard({
  card,
  dragging,
  canBeDeleted,
  ...props
}: Props) {
  const [selected, setSelected] = useState<boolean>(false);

  const classes = useStyles({ dragging, selected });

  const data = card.issue.consultantOpportunity;

  return (
    <div
      style={{
        width: "285px",
        boxSizing: "border-box",
        margin: "4px 8px"
      }}
    >
      <Card
        className={classes.root}
        onClick={() => props.onOpen()}
        onMouseEnter={() => {
          setSelected(true);
        }}
        onMouseLeave={() => {
          setSelected(false);
        }}
      >
        <Box className={classes.title}>
          <Typography variant="h6" style={{ fontWeight: 500 }} component="span">
            {card.title}{" "}
          </Typography>

          {selected && (
            <WithPopperSimple
              popperContent={({ close }) => (
                <ConsultantOpportunityCardMenu
                  withEditOpportunity={!isEmpty(data.weeks) || !!data.originalIssue}
                  withDeleteOpportunity={canBeDeleted}
                  onOpen={(e: MouseEvent) => {
                    e.stopPropagation();
                    props.onOpen();
                    close();
                  }}
                  onEditOpportunity={(e: MouseEvent) => {
                    e.stopPropagation();
                    props.onEditOpportunity();
                    close();
                  }}
                  onDeleteOpportunity={(e: MouseEvent) => {
                    e.stopPropagation();
                    props.onDeleteOpportunity();
                    close();
                  }}
                />
              )}
            >
              {({ open }) => (
                <IconButton
                  style={{
                    backgroundColor: Color.PrimaryMain,
                    borderRadius: "0.25rem",
                    padding: "0.25rem",
                    minWidth: "2rem",
                    height: "2rem"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                >
                  <EditFilledIcon className={classes.icon} />
                </IconButton>
              )}
            </WithPopperSimple>
          )}
        </Box>

        <Box className={classes.description}>
          {data.weeksString && (
            <>
              <Typography variant="body2" component="span">
                {locale.consultantOpportunityCard.presentedWeeks}:{" "}
                <span style={{ fontWeight: 500 }}>{data.weeksString}</span>
              </Typography>

              <Typography variant="body2" component="span">
                {locale.consultantOpportunityCard.replyDate}:{" "}
                <span style={{ fontWeight: 500 }}>
                  {data.dueDate ? moment(data.dueDate).format("YYYY-MM-DD") : "n/a"}
                </span>
              </Typography>
            </>
          )}

          {data.weeksWonString && (
            <Typography variant="body2" component="span">
              <CheckCircle
                style={{
                  color: Color.CheckGreen,
                  fontSize: "1rem",
                  top: "2px",
                  position: "relative",
                  marginRight: 4
                }}
              />
              {locale.consultantOpportunityCard.wonWeeks}:{" "}
              <span style={{ fontWeight: 500 }}>{data.weeksWonString}</span>
            </Typography>
          )}

          {data.weeksLostString && (
            <Typography variant="body2" component="span">
              <Notifications
                style={{
                  color: Color.ErrorLight,
                  fontSize: "1rem",
                  top: "2px",
                  position: "relative",
                  marginRight: 4
                }}
              />
              {locale.consultantOpportunityCard.lostWeeks}:{" "}
              <span style={{ fontWeight: 500 }}>{data.weeksLostString}</span>
            </Typography>
          )}

          {data.customer && (
            <Typography variant="body2" component="span">
              {locale.consultantOpportunityCard.org}:{" "}
              <span style={{ fontWeight: 500 }}>{data.customer.Name}</span>
            </Typography>
          )}

          <Typography variant="body2" component="span">
            {locale.consultantOpportunityCard.costCenter}:{" "}
            <span style={{ fontWeight: 500 }}>{data.costCenter?.Id}</span>
          </Typography>
          {data.description && (
            <Typography variant="body2" component="span">
              <span style={{ fontWeight: 500 }}>{data.description}</span>
            </Typography>
          )}
          {data.isShared && (
            <div className={classes.sharedIconWrap}>
              <AvatarGroup max={2}>
                {data.users.map((x, index) => (
                  <UserAvatar
                    key={index}
                    userName={x.Name}
                    size="mini"
                    style={{
                      marginLeft: index === 0 ? "0" : "-8px",
                      border: "1px solid #fff",
                      boxSizing: "content-box"
                    }}
                  />
                ))}
              </AvatarGroup>
            </div>
          )}
        </Box>
      </Card>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: (props: { dragging: boolean; selected: boolean }) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    borderRadius: "0.375rem",
    padding: "11px 17px",
    border: props.selected
      ? `1.5px solid ${Color.Information}`
      : `1px solid ${Color.LightGray}`,
    background: props.dragging ? transparentize(0.85, Color.PrimaryMain) : Color.OffWhite,
    boxShadow: `1px 6px 24px 0px ${Color.BoxShadow}`,
    "&:hover": {
      cursor: "pointer"
    }
  }),
  title: {
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "32px"
  },
  description: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem"
  },
  iconButton: {
    backgroundColor: Color.PrimaryMain,
    borderRadius: "0.25rem",
    padding: "0.25rem",
    minWidth: "2rem",
    height: "2rem",
    "&:hover": {
      backgroundColor: transparentize(0.5, Color.PrimaryMain)
    }
  },
  icon: {
    fill: Color.White
  },
  sharedIconWrap: {
    position: "absolute",
    bottom: "0.5rem",
    right: "0.5rem"
  }
}));
